import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { hasIn } from 'lodash';
import { NzDrawerRef, NzDrawerService } from 'ng-zorro-antd/drawer';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { take } from 'rxjs/operators';
import {
  DefaultConst,
  DeviceInfo,
  DeviceService,
  GetDeviceParams,
  MsisdnStatus,
  PaymentChannelType,
  SimonSimStatus,
  SimsV2Sim,
} from 'src/app/core';
import {
  GetEndUserLinesParams,
  Line,
  LinesService,
  LineStatus,
  UpdateLineParams,
} from 'src/app/core/proto/whitepages/lines';
import { GenericModalService } from 'src/app/core/services/generic-modal.service';
import { EndUsersMoreInfoComponent } from 'src/app/dashboard/mobile/end-users/end-users-overview/end-users-lines/end-users-more-info/end-users-more-info.component';
import { EndUsersSimSwapFormComponent } from 'src/app/dashboard/mobile/end-users/end-users-overview/end-users-lines/end-users-sim-swap-form/end-users-sim-swap-form.component';
import { EndUsersUpdatePhoneNumberFormComponent } from 'src/app/dashboard/mobile/end-users/end-users-overview/end-users-lines/end-users-update-phone-number-form/end-users-update-phone-number-form.component';
import { EndUsersMessagingComponent } from 'src/app/dashboard/mobile/end-users/end-users-overview/end-users-messaging/end-users-messaging.component';
import {
  actionEndUserLinesGet,
  actionEndUsersLineSelect,
} from 'src/app/dashboard/mobile/end-users/store/end-users.actions';
import { SimsV2SimKeyVal } from 'src/app/dashboard/mobile/end-users/store/end-users.reducer';
import { selectEndUserSelectedLine } from 'src/app/dashboard/mobile/end-users/store/end-users.selectors';
import {
  MobileEndUsersOverviewTab,
  MobileSimAndLineManagementLineTab,
  MobileSimAndLineManagementSimTab,
} from 'src/permissions';

@UntilDestroy()
@Component({
  selector: 'app-card-end-users-line',
  templateUrl: './card-end-users-line.component.html',
  styleUrls: ['./card-end-users-line.component.less'],
})
export class CardEndUsersLineComponent implements OnInit {
  SIM_PREFIX = 'PAGE.MOBILE.END_USERS.OVERVIEW.SIMS.';
  simStatus = SimonSimStatus;
  lineStatus = LineStatus;
  msisdnStatus = MsisdnStatus;
  MobileEndUsersOverviewTab = MobileEndUsersOverviewTab;
  MobileSimAndLineManagementSimTab = MobileSimAndLineManagementSimTab;
  MobileSimAndLineManagementLineTab = MobileSimAndLineManagementLineTab;
  drawerRef: NzDrawerRef<any>;
  modalRef?: NzModalRef;
  confirmModal?: NzModalRef;
  formatDate = DefaultConst.CARD_FORMAT_DATE;
  selectedLine: Line;
  translatedTexts: Array<string> = [];
  device: DeviceInfo;

  @Input() hasActions: boolean = true;
  @Input() brandConfigActivateOnAttach: boolean;
  @Input() consumerIdentityUuid: string;
  @Input() brandUuid: string;
  @Input() isSelectable: boolean = true;
  sim: SimsV2Sim;

  private _sims: SimsV2SimKeyVal;
  private _line: Line;

  get sims(): SimsV2SimKeyVal {
    return this._sims;
  }

  @Input() set sims(sims: SimsV2SimKeyVal) {
    this._sims = sims;
    if (hasIn(this.line, 'iccid') && sims) {
      this.sim = sims[this.line.iccid];
    }
  }

  get line(): Line {
    return this._line;
  }

  @Input() set line(line: Line) {
    this._line = line;

    this._getDeviceInfo();
  }

  constructor(
    private store: Store,
    private router: Router,
    private translateService: TranslateService,
    private modal: NzModalService,
    private linesService: LinesService,
    private drawerService: NzDrawerService,
    private genericModalService: GenericModalService,
    private deviceService: DeviceService
  ) {}

  ngOnInit(): void {
    this.getTranslatedText();
    if (this.isSelectable) {
      this.getSelectedLine();
    }
  }

  /**
   * Build string path for i18n
   * @param path
   */
  getStrPath(path) {
    return `${this.SIM_PREFIX}${path}`;
  }

  getTranslatedText() {
    this.translateService
      .stream([
        'BUTTON.CANCEL',
        'PAGE.MOBILE.END_USERS.UPDATE_PHONE_NUMBER.TITLE',
        'PAGE.MOBILE.END_USERS.UPDATE_PHONE_NUMBER.AUTO_SELECT_TITLE',
        'PAGE.MOBILE.END_USERS.UPDATE_PHONE_NUMBER.BUTTON',
        'PAGE.MOBILE.END_USERS.UPDATE_PHONE_NUMBER.CONFIRM_MODAL.CONFIRM_BUTTON',
      ])
      .pipe(untilDestroyed(this))
      .subscribe((translatedTexts) => {
        this.translatedTexts = translatedTexts;
      });
  }

  getStatusCode(status: LineStatus) {
    let statusCode = 'default';

    switch (status) {
      case LineStatus.ACTIVATED:
        statusCode = 'success';
        break;

      default:
        break;
    }

    return statusCode;
  }

  sendMessage() {
    this.translateService
      .stream('PAGE.MOBILE.END_USERS.SEND_MESSAGE.TITLE')
      .pipe(untilDestroyed(this))
      .subscribe((translatedTitle) => {
        this.drawerRef = this.drawerService.create<
          EndUsersMessagingComponent,
          { consumerIdentityUuid: string; brandUuid: string }
        >({
          nzTitle: translatedTitle,
          nzWidth: 400,
          nzContent: EndUsersMessagingComponent,
          nzContentParams: {
            lineId: this.line.lineId,
            brandUuid: this.brandUuid,
          },
          nzOnCancel: () =>
            new Promise(() => {
              this.genericModalService.warningFormModal(
                this.drawerRef.getContentComponent().messagingForm,
                {
                  ref: this.drawerRef,
                }
              );
            }),
        });
      });
  }

  swapSim() {
    this.translateService
      .stream(this.getStrPath('FORM_SIM_SWAP.TITLE'))
      .pipe(untilDestroyed(this))
      .subscribe((translatedTitle) => {
        this.drawerRef = this.drawerService.create<
          EndUsersSimSwapFormComponent,
          { oldIccid: string; lineId: string }
        >({
          nzTitle: translatedTitle,
          nzWidth: 400,
          nzContent: EndUsersSimSwapFormComponent,
          nzContentParams: {
            oldIccid: this.line.iccid,
            lineId: this.line.lineId,
          },
          nzOnCancel: () =>
            new Promise(() => {
              this.genericModalService.warningFormModal(
                this.drawerRef.getContentComponent().simSwapForm,
                {
                  ref: this.drawerRef,
                }
              );
            }),
        });
      });

    this.drawerRef.afterClose.subscribe((data) => {
      if (data && data.reloadData) {
        this.selectLine();
      }
    });
  }

  moreInfo() {
    this.translateService
      .stream(this.getStrPath('MORE_INFO.TITLE'))
      .pipe(untilDestroyed(this))
      .subscribe((translatedTitle) => {
        this.drawerRef = this.drawerService.create<
          EndUsersMoreInfoComponent,
          { consumerIdentityUuid: string; brandUuid: string }
        >({
          nzTitle: translatedTitle,
          nzWidth: 500,
          nzContent: EndUsersMoreInfoComponent,
          nzContentParams: {
            consumerIdentityUuid: this.consumerIdentityUuid,
            sim: this.sim,
            line: this.line,
            brandUuid: this.brandUuid,
            device: this.device,
            brandConfigActivateOnAttach: this.brandConfigActivateOnAttach,
          },
        });
      });
  }

  private getSelectedLine(): void {
    this.store
      .pipe(select(selectEndUserSelectedLine), untilDestroyed(this))
      .subscribe((selectedLine: Line) => {
        if (selectedLine) {
          this.selectedLine = selectedLine;
        }
      });
  }

  selectLine() {
    this.store.dispatch(actionEndUsersLineSelect({ selectedLine: this.line }));
  }

  onIccidClick(iccid: string): void {
    this.router.navigate([MobileSimAndLineManagementSimTab.absolutePath], {
      queryParams: { simSearch: iccid },
    });
  }

  onMsisdnClick(msisdn: string): void {
    this.router.navigate([MobileSimAndLineManagementLineTab.absolutePath], {
      queryParams: { lineSearch: parseInt(msisdn) },
    });
  }

  goToLineManagement(lineId: string): void {
    this.router.navigate([MobileSimAndLineManagementLineTab.absolutePath], {
      queryParams: { lineId: lineId },
    });
  }

  updatePhoneNumber() {
    this.modalRef = this.genericModalService.createModal({
      nzClosable: false,
      nzTitle:
        this.translatedTexts[`PAGE.MOBILE.END_USERS.UPDATE_PHONE_NUMBER.TITLE`],
      nzWidth: '500px',
      nzContent: EndUsersUpdatePhoneNumberFormComponent,
      nzComponentParams: {
        line: this.line,
        brandUuid: this.brandUuid,
      },
      nzFooter: [
        {
          label: this.translatedTexts['BUTTON.CANCEL'],
          onClick: () => {
            new Promise(() => {
              this.genericModalService.warningFormModal(
                this.modalRef.getContentComponent().updatePhoneNumberForm,
                {
                  ref: this.modalRef,
                }
              );
            });
          },
        },
        {
          label:
            this.translatedTexts[
              `PAGE.MOBILE.END_USERS.UPDATE_PHONE_NUMBER.BUTTON`
            ],
          type: 'primary',
          onClick: (that) => {
            new Promise(() => {
              this._showModalConfirm(that.updatePhoneNumberForm);
            });
          },
          disabled: (that) => !that.updatePhoneNumberForm.valid,
        },
      ],
      nzOnCancel: (that) =>
        this.genericModalService.warningFormModal(that.updatePhoneNumberForm, {
          ref: this.modalRef,
        }),
    });
  }

  private _showModalConfirm(form): void {
    const oldPhoneNumber = this.line?.phoneNumber?.phoneNumber;
    let newPhoneNumber;

    if (form.get('phoneNumberInput').value) {
      newPhoneNumber = form.get('phoneNumberInput').value.e164Number;
    } else {
      newPhoneNumber = form.get('phoneNumberSelect').value;
    }

    let titleText;
    let isRandomPhoneNumber = false;
    if (
      form.get('phoneNumberSelect').value ||
      form.get('phoneNumberInput').value
    ) {
      titleText = this.translateService.instant(
        `PAGE.MOBILE.END_USERS.UPDATE_PHONE_NUMBER.CONFIRM_MODAL.TITLE`,
        {
          oldPhoneNumber: oldPhoneNumber,
          newPhoneNumber: newPhoneNumber,
        }
      );
    } else {
      isRandomPhoneNumber = true;
      titleText = this.translateService.instant(
        `PAGE.MOBILE.END_USERS.UPDATE_PHONE_NUMBER.CONFIRM_MODAL.AUTO_SELECT_TITLE`,
        {
          oldPhoneNumber: oldPhoneNumber,
          phoneNumberFromAreaCode: form.get('areaCode').value,
        }
      );
    }

    this.confirmModal = this.modal.confirm({
      nzOkText:
        this.translatedTexts[
          `PAGE.MOBILE.END_USERS.UPDATE_PHONE_NUMBER.CONFIRM_MODAL.CONFIRM_BUTTON`
        ],
      nzCancelText: this.translatedTexts['BUTTON.CANCEL'],
      nzTitle: titleText,
      nzOnOk: () => {
        new Promise(() => {
          this.submitForm(
            newPhoneNumber,
            form.get('areaCode').value,
            isRandomPhoneNumber
          );
        });
      },
      nzOnCancel: () => {
        new Promise((resolve) => {});
      },
    });
  }

  submitForm(newPhoneNumber, areaCode, isRandomPhoneNumber) {
    this.modalRef.close();
    let updateLineParams: UpdateLineParams;

    if (isRandomPhoneNumber) {
      updateLineParams = {
        lineId: this.line.lineId,
        brandId: this.brandUuid,
        services: { plan: { paymentChannelType: PaymentChannelType.PORTAL } },
        phoneNumberFromAreaCode: areaCode,
      };
    } else {
      updateLineParams = {
        lineId: this.line.lineId,
        brandId: this.brandUuid,
        services: { plan: { paymentChannelType: PaymentChannelType.PORTAL } },
        phoneNumber: newPhoneNumber,
      };
    }

    this.linesService
      .updateLine(updateLineParams)
      .pipe(take(1))
      .subscribe(() => {
        this.dispatchEndUserLines();
      });
  }

  private dispatchEndUserLines() {
    const params: GetEndUserLinesParams = {
      endUserId: this.consumerIdentityUuid,
      optFields: ['brandId'],
    };

    this.store.dispatch(actionEndUserLinesGet({ params }));
  }

  private _getDeviceInfo(): void {
    if (hasIn(this.line, 'lineId')) {
      const getDevicesParams: GetDeviceParams = {
        lineId: this.line.lineId,
      };

      this.deviceService
        .getDevice(getDevicesParams)
        .pipe(untilDestroyed(this))
        .subscribe((device: DeviceInfo) => {
          this.device = device;
        });
    }
  }
}
